<template>
  <div
    class="factory-card__container"
    v-if="factory && factory.status !== 0"
  >
    <div class="factory-card__header">
      <h2 class="factory-card__title">
        Know Your Supplier
      </h2>
      <p class="factory-card__subtitle">
        Source with Confidence from Our Curated Network of Trusted Suppliers.
      </p>
    </div>

    <div class="factory-card">
      <div class="factory-card__main">
        <Image
          class="factory-card__logo"
          :src="factory.companyLogo ?? DefaultImage"
        />
        <div>
          <div class="factory-card__details">
            <div class="factory-card__name">
              <p class="factory-card__supplier">
                {{ factory.supplierName }}
              </p>
              <p class="factory-card__factory">
                {{ factory.companyName ?? '-' }}
              </p>
            </div>
            <Button
              class="factory-card-follow"
              :class="{
                'factory-card-follow--followed': isFollowed
              }"
              @click="onFollowClick"
              :loading="following"
            >
              <template v-if="isFollowed">
                <Icon
                  class="factory-card-follow__icon"
                  name="right-l"
                />
                Followed
              </template>
              <template v-else>
                Follow Factory
              </template>
            </Button>
          </div>
          <div class="factory-card__meta">
            <Icon
              class="factory-card__location"
              name="location-l"
            />
            <p>{{ factory.location ?? '-' }},</p>
            <img
              class="factory-card__country"
              src="https://cdn-image-old.bestdealer.com/cn.svg"
            >
            <span>China</span>
            <div
              class="factory-card__dot"
            />
            <span>{{ factory.honestMerchant ?? '-' }} years</span>
            <div class="factory-card__dot" />
            <span>{{ factory.employeesDesc ?? '-' }} employees</span>
            <div class="factory-card__dot" />
            <span>{{ factory.plantAreaDesc ?? '-' }}</span>
          </div>
        </div>
      </div>

      <div class="factory-card__card">
        <div class="factory-card-rating">
          <p class="factory-card-rating__title">
            Supplier Credit Rating
          </p>
          <div class="factory-card-rating__overview">
            <div>
              <template v-if="factory.supplierRatingVo">
                <p class="factory-card-rating-score">
                  <span class="factory-card-rating-score__rating">{{ factory.supplierRatingVo.compositeService.toFixed(1) }}</span>
                  <span class="factory-card-rating-score__divider">/</span>5
                </p>
                <StarRate
                  class="factory-card-rating__rate"
                  :value="factory.supplierRatingVo.compositeService"
                  type="black"
                />
              </template>
              <div
                v-else
                class="factory-card-rating-score"
              >
                -
              </div>
              <p class="factory-card-rating__supplier">
                Supplier Credit Rating
              </p>
            </div>
            <div>
              <div
                class="factory-card-rating__item"
                v-for="(item, key) in ratings"
                :key="key"
              >
                <p class="factory-card-rating__label">
                  {{ item.label }}
                </p>
                <p class="factory-card-rating__value">
                  {{ item.score ? item.score.toFixed(1) : '-' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="factory-card-factory">
          <p class="factory-card-factory__title">
            Factory Status
          </p>
          <div class="factory-card-factory__content">
            <div class="factory-card-factory__item">
              <p class="factory-card-factory__value">
                {{ factory.supplierRatingVo ? `${Math.ceil(factory.supplierRatingVo.repeatPurchasePercent * 100)}%` : '-' }}
              </p>
              <p class="factory-card-factory__desc">
                Last 90 days
              </p>
              <p class="factory-card-factory__label">
                Customer reorder rate
              </p>
            </div>
            <div class="factory-card-factory__item">
              <p class="factory-card-factory__value">
                {{ factory.customerCount ? formatSalesVolume(factory.customerCount) : '-' }}
              </p>
              <p class="factory-card-factory__desc">
                total
              </p>
              <p class="factory-card-factory__label">
                Store followers
              </p>
            </div>
            <div class="factory-card-factory__item">
              <p class="factory-card-factory__value">
                {{ factory.soldOut ? formatAmount(factory.soldOut) : '-' }}
              </p>
              <p class="factory-card-factory__desc">
                Last 90 days
              </p>
              <p class="factory-card-factory__label">
                Number of paid orders
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="factory-card__footer"
        style="display: none"
      >
        <div
          v-for="tag in tags"
          :key="tag"
          class="factory-card__feature"
        >
          <img src="@/assets/factory/checked.svg">
          <span>{{ tag }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Image } from '@/components/Image'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { formatSalesVolume, formatAmount } from '@/utils/utils'
import { StarRate } from '@/components/StarRate'
import { useFactory } from '@/components/SupplierHeader/useFactory'
import DefaultImage from '@/assets/factory/default-logo.svg'

const props = defineProps({
  factoryId: {
    type: String,
    required: true
  }
})
const factoryId = computed(() => props.factoryId)

const { factory, isFollowed, toggleFollow } = useFactory(factoryId)

const ratings = computed(() => [
  { label: 'Trade Medal Level', score: factory.value?.supplierRatingVo?.tradeMedalLevel },
  { label: 'Quality Experience', score: factory.value?.supplierRatingVo?.qualityExperience },
  { label: 'Composite Service', score: factory.value?.supplierRatingVo?.compositeService },
  { label: 'Dispute Complaint', score: factory.value?.supplierRatingVo?.disputeComplaint }
])

const tags = [
  'Fastest Response',
  'ISO Certified for Quality Assurance',
  'Flexible MOQ, Fast and Adaptive Delivery'
]

const following = ref(false)

const onFollowClick = async () => {
  following.value = true
  await toggleFollow('Detail')
  following.value = false
}
</script>

<style lang="less" scoped>
.factory-card {
  position: relative;
  padding: 20px 24px;
  border-radius: 0 12px 12px 12px;
  background-color: #F4F7FF;
  .factory-card-follow {
    padding-left: 8px;
    padding-right: 8px;
    width: auto;
  }
  &::after {
    display: none;
    content: '';
    position: absolute;
    top: 20px;
    right: 58px;
    width: 112px;
    height: 112px;
    background: url('@/assets/factory/factory-card-badge.png') no-repeat center / contain;
  }

  @media (min-width: 1280px) {
    background: url('@/assets/factory/factory-card-mask.png') no-repeat top right / contain;
    background-color: #F4F7FF;
    background-size: 280px 280px;
    background-position: top -50px right -16px;

    &::after {
      display: block;
    }
  }

  &__container {
    background: #fff;
    border-radius: 12px 12px 30px 30px;
    background: linear-gradient(112.89deg, #4452A1 0%, #191E3B 64.84%);
    color: #232323;
  }

  &__header {
    padding-top: 9px;
    display: flex;
  }

  &__title {
    flex-shrink: 0;
    margin-left: -1px;
    padding: 15px 0 0 24px;
    width: 295px;
    height: 47px;
    background: url('@/assets/factory/tab.svg') no-repeat center / contain;
    font-family: 'Young Serif', serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 24px;
  }

  &__subtitle {
    padding: 15px 24px 0 4px;
    font-family: mon-600;
    font-size: 12px;
    line-height: 16px;
    color: #FFFDF2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__main {
    display: flex;
    gap: 16px;
  }

  &__logo {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 50%;

    &--default {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #D3E1FD;

      > img {
        width: 45px;
        height: 45px;
      }
    }
  }

  &__details {
    display: flex;
    gap: 16px;
  }

  &__supplier {
    font-family: Young Serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
  }

  &__factory {
    margin-top: 5px;
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
  }

  &-follow {
    width: 112px;
    height: 28px;
    font-family: mon-600;
    font-size: 12px;
    line-height: 16px;
    background-color: #232323;
    color: #FFFFFF;
    border-radius: 4px;
    border: none;
    gap: 2px;

    &__icon {
      font-size: 16px;
    }

    &--followed {
      background-color: #F5F5F5;
      color: #232323;
    }
  }

  &__meta {
    margin-top: 6px;
    font-family: mon-500;
    font-size: 11px;
    line-height: 14px;
    display: flex;
    align-items: center;
  }

  &__location {
    margin-right: 2px;
    font-size: 16px;
  }

  &__country {
    margin-left: 6px;
    margin-right: 4px;
    width: 16px;
    height: 16px;
  }

  &__dot {
    margin: 0 6px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: #494D51;
  }

  &__card {
    margin-top: 16px;
    padding: 16px;
    border-radius: 8px;
    background-color: #FFF;
    display: flex;
    justify-content: space-between;
    gap: 24px;

    @media (min-width: 1440px) {
      padding: 16px 24px;
      gap: 85px;
      justify-content: flex-start;
    }
  }

  &-rating {
    min-width: 280px;
    max-width: 320px;

    &__title {
      font-family: mon-700;
      font-size: 14px;
      line-height: 18px;
    }

    &__overview {
      display: flex;
      gap: 8px;
      padding-top: 18px;

      @media (min-width: 1280px) {
        gap: 43px;
      }
    }

    &-score {
      margin-top: 4px;
      font-family: mon-700;
      font-size: 14px;
      line-height: 18px;
      color: #232323;

      &__rating {
        font-size: 24px;
        line-height: 28px;
      }

      &__divider {
        margin-left: 2px;
        font-size: 16px;
        line-height: 20px;
      }
    }

    &__rate {
      margin-top: 6px;
    }

    &__supplier {
      margin-top: 8px;
      font-family: mon-500;
      font-size: 11px;
      line-height: 14px;
      color: #6E717A;
    }

    &__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 148px;

      + .factory-card-rating__item {
        margin-top: 4px;
      }
    }

    &__label {
      font-family: mon-500;
      font-size: 11px;
      line-height: 14px;
      color: #6E717A;
    }

    &__value {
      font-family: mon-600;
      font-size: 12px;
      line-height: 16px;
      color: #232323;
    }
  }

  &-factory {
    min-width: 280px;
    max-width: 395px;

    &__title {
      font-family: mon-700;
      font-size: 14px;
      line-height: 18px;
    }

    &__content {
      margin-top: 6px;
      padding: 11px 0 4px;
      display: flex;
      gap: 8px;

      @media (min-width: 1280px) {
        gap: 16px;
      }
    }

    &__item {
      min-width: 88px;
    }

    &__value {
      font-family: mon-700;
      font-size: 24px;
      line-height: 28px;
      color: #232323;
    }

    &__desc {
      margin-top: 2px;
      font-family: mon-500;
      font-size: 11px;
      line-height: 14px;
      color: #6E717A;
    }

    &__label {
      margin-top: 15px;
      font-family: mon-500;
      font-size: 11px;
      line-height: 14px;
      color: #6E717A;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__footer {
    margin-top: 20px;
    display: flex;
    gap: 24px;
  }

  &__feature {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #232323;
  }
}
</style>
